import React, { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DynamicField from './DynamicField';
import { useDynamicValidation } from '../../hooks/useDynamicValidation';
import { useConditionalLogic } from '../../hooks/useConditionalLogic';
import { ModernButton } from '../ui/modern';

function buildDefaults(fields, initialValues) {
  const defaults = {};
  (fields || []).forEach((field) => {
    const key = field.fieldKey;
    if (!key) return;
    if (initialValues && Object.prototype.hasOwnProperty.call(initialValues, key)) {
      defaults[key] = initialValues[key];
      return;
    }
    if (field.defaultValue != null) {
      defaults[key] = field.defaultValue;
      return;
    }
    if (field.fieldType === 'checkbox') {
      defaults[key] = false;
      return;
    }
    if (field.fieldType === 'multiselect') {
      defaults[key] = [];
      return;
    }
    defaults[key] = '';
  });
  return defaults;
}

export default function DynamicForm({ fields, initialValues, onSubmit, onCancel, isSubmitting }) {
  const schema = useDynamicValidation(fields);
  const defaultValues = useMemo(() => buildDefaults(fields, initialValues), [fields, initialValues]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const values = useWatch({ control });
  const visibility = useConditionalLogic(fields, values);
  const visibleFields = useMemo(() => {
    return (fields || []).filter((field) => {
      if (!field.fieldKey) return false;
      if (field.group === 'hidden') return false;
      if (visibility[field.fieldKey] === false) return false;
      return true;
    });
  }, [fields, visibility]);
  const standardFields = visibleFields.filter((field) => field.group !== 'advanced');
  const advancedFields = visibleFields.filter((field) => field.group === 'advanced');
  const hasAdvanced = advancedFields.length > 0;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {standardFields.map((field) => {
          return (
            <DynamicField
              key={field.id || field.fieldKey}
              field={field}
              register={register}
              control={control}
              error={errors[field.fieldKey]?.message}
            />
          );
        })}
      </div>
      {hasAdvanced && (
        <div className="border-t border-white/10 pt-4 space-y-3">
          <button
            type="button"
            className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
            onClick={() => setShowAdvanced((prev) => !prev)}
          >
            <span>{showAdvanced ? 'Hide advanced' : 'Show advanced'}</span>
            <span className="text-xs text-slate-400">({advancedFields.length})</span>
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {advancedFields.map((field) => (
                <DynamicField
                  key={field.id || field.fieldKey}
                  field={field}
                  register={register}
                  control={control}
                  error={errors[field.fieldKey]?.message}
                />
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex justify-end gap-3 pt-2">
        {onCancel && (
          <ModernButton variant="glass" type="button" onClick={onCancel}>
            Cancel
          </ModernButton>
        )}
        <ModernButton variant="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save'}
        </ModernButton>
      </div>
    </form>
  );
}


